import React, { createContext, useContext, useEffect, useState } from 'react';
import api from '../api/axiosConfig';
import { safeJsonGet, safeJsonSet } from '../utils/safeStorage';
import { configureMetaPixel } from '../utils/metaPixel';
import { configureTikTokPixel } from '../utils/tiktokPixel';
import { startCustomScripts } from '../utils/customScripts';
const KEY = 'jlastore.site-links.v1';
const EMPTY = { links: [], pixels: {} };
const Context = createContext(EMPTY);
let pending = null;
export const useSiteConfig = () => useContext(Context);
export const linksFor = (config, placement) => config.links.filter(link => link.enabled && link.placements.includes(placement));
// Check the public response again before creating clickable URLs, including stored browser data.
function safeLinks(links) {
  return Array.isArray(links) ? links.filter(link => {
    try { const url = new URL(link.url); return url.protocol === 'https:' && !url.username && !url.password && typeof link.label === 'string' && Array.isArray(link.placements); } catch (_) { return false; }
  }).slice(0, 50) : [];
}
function updateSocialSchema(links) {
  const script = document.getElementById('storefront-site-schema');
  if (!script) return;
  try {
    const schema = JSON.parse(script.textContent);
    const organization = schema['@graph']?.find(node => node['@type'] === 'Organization');
    if (organization) {
      organization.sameAs = [...new Set(links.filter(link => ['telegram', 'facebook', 'tiktok', 'instagram', 'youtube', 'website'].includes(link.type)).map(link => link.url))];
      script.textContent = JSON.stringify(schema);
    }
  } catch (_) { /* SEO metadata must not affect rendering. */ }
}
function load() {
  if (!pending) pending = api.get('/store/site-config', { cache: false, storefrontEncryption: false, timeout: 5000, getRetryCount: 0 }).then(response => {
    if (response.data.status !== 'success' || !response.data.data?.pixels) throw new Error('Settings unavailable');
    return { ...response.data.data, links: safeLinks(response.data.data.links) };
  }).finally(() => { pending = null; });
  return pending;
}
export default function SiteConfigProvider({ children }) {
  const [config, setConfig] = useState(() => {
    const saved = safeJsonGet(KEY);
    return saved && Date.now() - saved.saved_at < 86400000 ? { ...EMPTY, links: safeLinks(saved.links) } : EMPTY;
  });
  useEffect(() => {
    let active = true, idle, timer;
    const refresh = async () => {
      if (document.hidden) return;
      try {
        const data = await load(); if (!active) return;
        setConfig(data); updateSocialSchema(data.links); safeJsonSet(KEY, { saved_at: Date.now(), links: data.links });
        // Pixels use fresh server settings, never browser-cached IDs. SDKs are async and start after paint.
        const configure = () => {
          if (!active) return;
          // A provider SDK failure cannot prevent other independent integrations from starting.
          for (const start of [() => configureMetaPixel(data.pixels.meta), () => configureTikTokPixel(data.pixels.tiktok), () => startCustomScripts(data.scripts || [])]) {
            try { start(); } catch (_) { /* Analytics must never interrupt the storefront. */ }
          }
        };
        if (idle) window.cancelIdleCallback?.(idle);
        clearTimeout(timer);
        if (window.requestIdleCallback) idle = window.requestIdleCallback(configure, { timeout: 1500 });
        else timer = setTimeout(configure, 100);
      } catch (_) { /* Keep useful cached links; settings failures never block pages or checkout. */ }
    };
    refresh();
    const interval = setInterval(refresh, 60000);
    document.addEventListener('visibilitychange', refresh);
    return () => { active = false; clearInterval(interval); clearTimeout(timer); window.cancelIdleCallback?.(idle); document.removeEventListener('visibilitychange', refresh); };
  }, []);
  return <Context.Provider value={config}>{children}</Context.Provider>;
}
